import { RemoteConfigModel } from 'src/models/remoteConfig.models'
import type { CapabilityDecisionResponse } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import {
  isCardManagementActionAvailable,
  isCardManagementViewAvailable,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'

/**
 * Check if SSO Login feature is enabled for the user.
 * Returns true if either:
 * 1. The global enable_sso_login flag is enabled, OR
 * 2. The user's organization ID is in the enable_sso_login_whitelist
 *
 * Note: On the sign-in page, organizationId may not be available yet.
 * In that case, only the global flag is checked.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional for sign-in page)
 * @returns true if SSO Login is enabled for this user
 */
export const isSSOLoginEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  const { enable_sso_login, enable_sso_login_whitelist } = remoteConfig

  // If org ID is available, check the whitelist
  if (organizationId) {
    return (
      enable_sso_login || enable_sso_login_whitelist.includes(organizationId)
    )
  }

  // On sign-in page (no org ID), only use the global flag
  return enable_sso_login
}

/**
 * Check if AI Insights feature is enabled for the user.
 * Returns true if either:
 * 1. The global show_ai_insights flag is enabled, OR
 * 2. The user's organization ID is in the ai_insights_allowlist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if AI Insights is enabled for this user
 */
export const isAIInsightsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const { show_ai_insights, ai_insights_allowlist } = remoteConfig

  // Feature is enabled if the global flag is on OR the org is in the allowlist
  return show_ai_insights || ai_insights_allowlist.includes(organizationId)
}

/**
 * Check if Extend AI Agent feature is enabled for the user.
 * Returns true if either:
 * 1. The global show_extend_ai_agent flag is enabled, OR
 * 2. The user's organization ID is in the extendAIWhitelist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if Extend AI Agent is enabled for this user
 */
export const isExtendAIEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const { show_extend_ai_agent, extendAIWhitelist } = remoteConfig

  // Feature is enabled if the global flag is on OR the org is in the allowlist
  return show_extend_ai_agent || extendAIWhitelist.includes(organizationId)
}

/**
 * Check if the spend reporting dashboard is enabled for the user.
 * Returns true if either:
 * 1. The global enable_spend_reporting_dashboard flag is enabled, OR
 * 2. The user's organization ID is in the
 *    enable_spend_reporting_dashboard_whitelist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if the spend reporting dashboard is enabled for this user
 */
export const isSpendReportingDashboardEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const {
    enable_spend_reporting_dashboard,
    enable_spend_reporting_dashboard_whitelist,
  } = remoteConfig

  return (
    enable_spend_reporting_dashboard ||
    enable_spend_reporting_dashboard_whitelist.includes(organizationId)
  )
}

/** Show transaction comments when the client feature flag is enabled. */
export const isTransactionConversationsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return remoteConfig.enable_transaction_conversations
}

/** Use save-on-blur for transaction free-text expense categories when enabled. */
export const isTransactionFreeTextSaveOnBlurEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => remoteConfig?.enable_transaction_free_text_save_on_blur === true

export const isExpensePolicyAIEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  const { show_expense_policy_ai, show_expense_policy_ai_whitelist } =
    remoteConfig

  if (organizationId) {
    return (
      show_expense_policy_ai ||
      show_expense_policy_ai_whitelist.includes(organizationId)
    )
  }

  return show_expense_policy_ai
}

export const isExpensePolicyAIChatEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return remoteConfig.show_expense_policy_ai_chat
}

export const isGoogleWorkspaceIntegrationsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  return remoteConfig?.show_google_workspace_integrations === true
}

/**
 * Check if the redesigned physical card Account Settings tiles are enabled.
 * Gates the new bento layout (Card Status & Lifecycle, Spend Limit, etc.) on
 * the physical card details page. EX-40354.
 *
 * Client flag OR the server's demo-enrollment verdict
 * (`VIEW_PHYSICAL_CARD_MANAGEMENT`), matching the hero and the Statements /
 * Activity tabs (EX-40599). The bento is the surface those tiles' per-action
 * capabilities describe, so an org the server has enrolled is entitled to it
 * without a flag rollout; the flag stays a unilateral override for everyone
 * else.
 *
 * BOTH CALLERS MUST PASS THE SAME CAPABILITIES. This gate is read twice — by
 * `AccountEntityPage` to rename the tab "Controls & Settings", and by
 * `AccountSettings` to pick the bento over the legacy boxes. They must agree, or
 * a user lands on a tab whose name promises a surface it does not render. Both
 * read the same `capabilities(creditCardId)` cache entry, so they do.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view;
 *   undefined while the query is pending, errored, or disabled, which degrades
 *   the gate to the flag alone
 * @returns true if the updated physical card settings UI is enabled
 */
export const isUpdatedPhysicalCardSettingsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_physical_card_settings === true ||
  isCardManagementViewAvailable(capabilities)

/**
 * Check if the self-service confirm-card-details flow is enabled. EX-40641.
 *
 * Gates only the missing-details entry points (panel/row visibility for
 * status=null cards and routing into ConfirmCardDetailsModal); the standard
 * connect flow for cards with complete details is unaffected.
 *
 * @param remoteConfig - the remote config model
 * @returns true if the confirm-card-details flow is enabled
 */
export const isCardDetailsConfirmationEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => remoteConfig?.card_details_confirmation === true

/**
 * Check if the card-detail Statements tab is enabled. EX-40600.
 *
 * Client flag OR the server's `READ_PHYSICAL_CARD_STATEMENTS` capability, so an
 * enrolled org sees the tab with the flag off and the flag stays a unilateral
 * override for everyone else. The capability half landed with the backend's
 * view-capability slice; before that the contract had no statements action at
 * all and this gate was flag-only.
 *
 * Enrollment and servable limits are separate preconditions on the server — an
 * enrolled non-USD card has its limit actions UNAVAILABLE while the read-only
 * view actions stay AVAILABLE — so this must key on its own action and never
 * treat another action's verdict as a proxy.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view;
 *   undefined while the query is pending, errored, or disabled, which degrades
 *   the gate to the flag alone
 * @returns true if the Statements tab should render
 */
export const isUpdatedCardStatementsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_card_statements === true ||
  isCardManagementActionAvailable(capabilities, 'READ_PHYSICAL_CARD_STATEMENTS')

/**
 * Check if the card-detail Activity tab is enabled. EX-40600.
 *
 * Same shape as {@link isUpdatedCardStatementsEnabled}, keyed on
 * `READ_PHYSICAL_CARD_ACTIVITY`. The two tabs stay independent — separate flags
 * and separate actions — so they can ship separately and so an org enrolled for
 * one is not handed the other.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view
 * @returns true if the Activity tab should render
 */
export const isUpdatedCardActivityEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_card_activity === true ||
  isCardManagementActionAvailable(capabilities, 'READ_PHYSICAL_CARD_ACTIVITY')

/**
 * Whether the card detail header renders at all. EX-40350/40351/40352.
 *
 * One gate for the whole hero. It *replaces* the legacy AccountHeader and every
 * region (summary, card display, actions menu) renders together, so there is no
 * per-region visibility gate — on shows all of it, off shows the legacy header.
 * What each region *reads* is a separate question, answered by
 * {@link isLiveDataCardHeaderEnabled}.
 *
 * Client flag OR the server's demo-enrollment verdict, the same shape as
 * {@link isUpdatedCardStatementsEnabled} and {@link isUpdatedCardActivityEnabled}
 * (EX-40599). This gate was flag-only until now for the reason recorded on
 * {@link isUpdatedCardOverviewEnabled} — "no action means *may view the
 * redesigned surface*" — which is no longer true:
 * `VIEW_PHYSICAL_CARD_MANAGEMENT` is exactly that verdict, and it is what the
 * API already uses to decide whether this card is in the demo at all. ORing it
 * in means an enrolled org sees the hero without a flag rollout, while the flag
 * stays a unilateral override for everyone else.
 *
 * Keyed on the VIEW action alone and never on another action's verdict: the
 * server decides enrollment and per-action servability separately, so an
 * enrolled card can have, say, its limit actions UNAVAILABLE while it is still
 * entitled to the surface.
 *
 * TIMING: the capability arrives asynchronously and fails closed, so an enrolled
 * org with the flag off renders the legacy header for as long as the
 * capabilities query is in flight, then swaps. That is a visible swap, not a
 * correctness problem — and it is why the flag remains the way to get a
 * flicker-free rollout.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view;
 *   undefined while the query is pending, errored, or disabled, which degrades
 *   the gate to the flag alone
 * @returns true if the card detail header should replace the legacy header
 */
export const isUpdatedCardSummaryEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_card_summary === true ||
  isCardManagementViewAvailable(capabilities)

/**
 * Which source the card detail header reads from. EX-40596.
 *
 * One selector for both live-backed regions — the card-details reveal and the
 * Available credit figure — so the hero switches between "fully demo" and
 * "fully live" as a unit rather than presenting a mix.
 *
 * This selects a SOURCE; it is not a visibility gate. Both regions render
 * either way: false (the default) serves demo credentials and demo limits, true
 * reads live Card Management data. Turning it off must never remove an
 * affordance, only change what backs it.
 *
 * The two backends are at different stages, and turning this on surfaces that.
 * `READ_PHYSICAL_CARD_LIMIT` is real, so limits go genuinely live — including a
 * legitimate "unavailable" for an unenrolled or unsupported-currency card.
 * `READ_PHYSICAL_CARD_DETAILS` is provisional and the server emits no such
 * action, so the reveal reports unavailable until EX-40580 ships.
 *
 * Deliberately flag-ONLY, unlike {@link isUpdatedCardStatementsEnabled} and
 * {@link isUpdatedCardActivityEnabled} which OR their flag with a server
 * capability: there is no single action that means "the hero may read live
 * data", and ORing the limits action alone would let an enrolled card promote
 * the card-details reveal too.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @returns true if the hero should read live card data rather than demo data
 */
export const isLiveDataCardHeaderEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => remoteConfig?.show_live_data_card_header === true
/**
 * Check if the redesigned budget table is enabled. EX-40737.
 *
 * Governs BOTH budget tables on the card-detail page — the Overview tab's
 * snapshot and the Budgets tab's full list — as one gate, because they are the
 * same table under two headings. Splitting them would let a user read a budget's
 * spend against its limit on Overview and then land on a Budgets tab that
 * reports exposure and remaining instead, for the same rows.
 *
 * Client flag OR `VIEW_PHYSICAL_CARD_MANAGEMENT`, the server's demo-enrollment
 * verdict — the same shape as {@link isUpdatedCardSummaryEnabled} and
 * {@link isUpdatedPhysicalCardSettingsEnabled}. Keyed on the VIEW action rather
 * than a read action of its own: the budget rows come from the ordinary
 * `/creditcards` list, not from Card Management, so no `CardManagementAction`
 * describes them. What enrollment answers here is "is this card on the
 * redesigned surface", which is exactly what the VIEW verdict means.
 *
 * Unlike {@link isUpdatedCardOverviewEnabled} this is a pure CONTENT swap that
 * never touches the tab set: both tabs exist either way, and off renders the
 * legacy `BudgetsBox` untouched.
 *
 * TIMING: the capability arrives asynchronously and fails closed, so an enrolled
 * org with the flag off renders the legacy table until the capabilities query
 * settles, then swaps. Visible, not a correctness problem — and the reason the
 * flag remains the way to get a flicker-free rollout.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view;
 *   undefined while the query is pending, errored, or disabled, which degrades
 *   the gate to the flag alone
 * @returns true if the redesigned budget table should render
 */
export const isUpdatedBudgetsTableEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_budgets_table === true ||
  isCardManagementViewAvailable(capabilities)

/**
 * Check if the redesigned card-detail Overview tab is enabled. EX-40632/40633.
 *
 * Differs from the Statements and Activity gates in what it controls: those add
 * a tab, so flag-off renders NotFound. This one swaps the CONTENT of a tab that
 * already exists, so flag-off renders the legacy `AccountOverview` untouched.
 * It never removes Overview from the Subnav, and a direct URL resolves either
 * way.
 *
 * One exception to "content only": physical-only cards have no Overview tab on
 * the legacy path, because the legacy body summarised virtual cards and budgets
 * and redirected them away. The redesigned surface is card-level, so they GAIN
 * the tab when this is on. That is the single way this gate touches the tab set,
 * and it is why the off path keeps the legacy redirect.
 *
 * Client flag OR `VIEW_PHYSICAL_CARD_MANAGEMENT`, the server's demo-enrollment
 * verdict — the same shape as {@link isUpdatedCardSummaryEnabled},
 * {@link isUpdatedPhysicalCardSettingsEnabled} and
 * {@link isUpdatedBudgetsTableEnabled}.
 *
 * This was deliberately flag-ONLY until EX-40737, on the reasoning that
 * `CardManagementAction` had no member meaning "may view the redesigned
 * Overview". That reasoning is stale: `VIEW_PHYSICAL_CARD_MANAGEMENT` is
 * precisely that verdict — it is what the API already uses to decide whether a
 * card is in the demo at all — and it is what every sibling surface on this page
 * now keys on. Leaving Overview out meant an enrolled org got the redesigned
 * hero, settings, statements, activity and budget tables while Overview alone
 * stayed legacy.
 *
 * Keyed on the VIEW action alone and never on another action's verdict: the
 * server decides enrollment and per-action servability separately, so an
 * enrolled card can have its limit actions UNAVAILABLE while still being
 * entitled to the surface.
 *
 * TIMING: capabilities arrive asynchronously and fail closed, so an enrolled org
 * with the flag off renders legacy Overview until the query settles, then swaps.
 * For a PHYSICAL-ONLY card that swap also adds the Overview tab to the Subnav —
 * visible, but safe, because the gate only ever ADDS a tab here and can never
 * unmount a route the user is already sitting on. The flag remains the way to
 * get a flicker-free rollout.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view;
 *   undefined while the query is pending, errored, or disabled, which degrades
 *   the gate to the flag alone
 * @returns true if the redesigned Overview content should render
 */
export const isUpdatedCardOverviewEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_card_overview === true ||
  isCardManagementViewAvailable(capabilities)

/**
 * Check if Suggestions and Predictions feature is enabled for the user.
 * Requires all of:
 * 1. The global show_suggestions_and_predictions kill switch is enabled, AND
 * 2. The user has an active ERP integration.
 *
 * Non-SaaS issuer exclusion is handled upstream by the backend
 * AI_EXPENSE_CATEGORIES entitlement; this util only enforces the kill switch
 * and ERP-connected requirement.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @param isErpConnected - Whether the org has an active ERP integration
 * @returns true if Suggestions and Predictions is enabled for this user
 */
export const isSuggestionsAndPredictionsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
  isErpConnected: boolean,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  return remoteConfig.show_suggestions_and_predictions && isErpConnected
}

/**
 * Check if the ERP disconnect AI-loss warning modal is enabled.
 * EX-39491 — when on, the final disconnect confirmation shows a scary
 * AI-aware warning and requires the admin to type "DISCONNECT".
 */
export const isErpDisconnectAiWarningEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return remoteConfig.show_erp_disconnect_ai_warning
}

/**
 * Whole-feature Bill Pay gate (the new ACH/wire/check bank-account rails that
 * replace the legacy virtual-card bill pay — intentionally NOT gated by the
 * legacy show_bill_pay_* flags). Returns true if either:
 * 1. The global `bill_pay` flag is enabled, OR
 * 2. The org is on `bill_pay_allowlist` — the ONE milestone-agnostic Bill Pay
 *    beta list (an org on it is "in the Bill Pay beta", full stop).
 *
 * This gate alone shows nothing: each milestone surface ANDs it with its own
 * `bill_pay_mX_*` readiness bool (isBillPayM1BankAccountsEnabled,
 * isBillPayM2VendorsEnabled). Beta orgs receive each milestone the moment its
 * bool flips, with no per-milestone allowlists to maintain.
 *
 * When no organizationId is supplied (defensive / future caller) only the global
 * flag is checked. Never throws: a missing allowlist falls back to an empty list.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional; enables the allowlist path)
 * @returns true if this user's org passes the whole-feature Bill Pay gate
 */
export const isBillPayEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  const { bill_pay, bill_pay_allowlist } = remoteConfig

  // If org ID is available, also honor the per-org allowlist
  if (organizationId) {
    return bill_pay || (bill_pay_allowlist ?? []).includes(organizationId)
  }

  // No org ID (defensive / future caller): only use the global flag
  return bill_pay
}

/**
 * Check if Bill Pay M1 (Bank Accounts — the org funding-account experience) is
 * enabled for the user: (whole-feature gate) AND (`bill_pay_m1_bank_accounts`
 * "M1 is ready" bool). The bool keeps milestones releasable independently.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional; enables the allowlist path)
 * @returns true if Bill Pay Bank Accounts is enabled for this user
 */
export const isBillPayM1BankAccountsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return (
    isBillPayEnabled(remoteConfig, organizationId) &&
    remoteConfig.bill_pay_m1_bank_accounts
  )
}

/**
 * Check if Bill Pay M2 (Vendors) is enabled for the user: (whole-feature gate)
 * AND (`bill_pay_m2_vendors` "M2 is ready" bool).
 *
 * The AND keeps milestones releasable independently (M1 can GA while M2 is
 * dark; while M2 is mock-backed/in-build the bool stays off so beta orgs don't
 * see it) and encodes the product dependency: Vendors cannot exist without the
 * Bill Pay bank-account experience.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional; enables the allowlist path)
 * @returns true if Bill Pay Vendors is enabled for this user
 */
export const isBillPayM2VendorsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return (
    isBillPayEnabled(remoteConfig, organizationId) &&
    remoteConfig.bill_pay_m2_vendors
  )
}

/**
 * Check if Bill Pay M3 (Payments — the pay-a-vendor experience) is enabled for
 * the user: (whole-feature gate) AND (`bill_pay_m3_payments` "M3 is ready"
 * bool).
 *
 * Same shape as M1/M2 so milestones stay independently releasable.
 *
 * NOT enforced here: a payment needs a funding bank account (M1) and a payable
 * vendor (M2), but this gate does not check either bool — turning M3 on for an
 * org without M1/M2 yields a reachable page with nothing to pay from or to.
 * Ordering the rollout is a release-time decision, deliberately not encoded, so
 * the three milestone gates stay independent and individually reversible.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional; enables the allowlist path)
 * @returns true if Bill Pay Payments is enabled for this user
 */
export const isBillPayM3PaymentsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return (
    isBillPayEnabled(remoteConfig, organizationId) &&
    remoteConfig.bill_pay_m3_payments
  )
}
